import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, Outlet } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { FloatingActions } from "@/components/FloatingActions";
import { CookieConsent } from "@/components/CookieConsent";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Industries from "@/pages/Industries";
import KnowledgeCentre from "@/pages/KnowledgeCentre";
import ArticlePage from "@/pages/ArticlePage";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AuthCallback from "@/pages/admin/AuthCallback";

function useLenis() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function SiteLayout() {
  return (
    <div className="pb-[52px] md:pb-0">
      <Navbar />
      <main>
        <Outlet />
      </main>
      <Footer />
      <FloatingActions />
      <CookieConsent />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const location = useLocation();
  const [auth, setAuth] = useState(
    location.state?.user ? { status: "ok", user: location.state.user } : { status: "checking", user: null }
  );

  useEffect(() => {
    if (location.state?.user) return;
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) throw new Error("unauthorized");
        const user = await r.json();
        if (!cancelled) setAuth({ status: "ok", user });
      })
      .catch(() => {
        if (!cancelled) setAuth({ status: "no", user: null });
      });
    return () => {
      cancelled = true;
    };
  }, [location.state]);

  if (auth.status === "checking") {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center" data-testid="admin-auth-loading">
        <div className="w-10 h-10 rounded-full border-2 border-navy border-t-transparent animate-spin" />
      </div>
    );
  }
  if (auth.status === "no") return <Navigate to="/admin/login" replace />;
  return children(auth.user);
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<SiteLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/services" element={<Services />} />
        <Route path="/industries" element={<Industries />} />
        <Route path="/knowledge-centre" element={<KnowledgeCentre />} />
        <Route path="/knowledge-centre/:slug" element={<ArticlePage />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/:page(privacy-policy|terms-and-conditions|disclaimer|cookie-policy)" element={<Legal />} />
        <Route path="*" element={<NotFound />} />
      </Route>
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<ProtectedRoute>{(user) => <AdminDashboard user={user} />}</ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  useLenis();
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
